import { useParams, Link, useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ArticleCard from '@/components/feature/ArticleCard';
import SchemaOrg from '@/components/base/SchemaOrg';
import ScrollToTop from '@/components/base/ScrollToTop';
import { articles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';
import { getArticleImageUrl } from '@/mocks/articleImages';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-24 md:pt-28 pb-12 md:pb-16">
          <div className="w-full px-4 md:px-6 text-center">
            <div className="w-16 h-16 mx-auto bg-background-100 rounded-full flex items-center justify-center mb-4">
              <i className="ri-file-unknow-line text-2xl text-foreground-400"></i>
            </div>
            <h1 className="font-heading text-xl text-foreground-950 mb-2">文章未找到</h1>
            <p className="text-sm text-foreground-500 mb-4">该文章可能已被移除或链接无效</p>
            <button
              onClick={() => navigate('/category')}
              className="px-4 py-2 rounded-md bg-primary-500 text-background-50 text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              返回文章列表
            </button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const category = categories.find((c) => c.slug === article.category);
  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.slug !== article.slug)
    .slice(0, 3);

  const imageUrl = getArticleImageUrl(article.imageSeq);

  const breadcrumbItems = [
    { label: '首页', path: '/' },
    { label: '文章分类', path: '/category' },
    { label: category?.name || article.category, path: `/category/${article.category}` },
    { label: article.title },
  ];

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: imageUrl,
    datePublished: article.publishDate,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    publisher: {
      '@type': 'Organization',
      name: '膀胱健康管理网',
    },
    inLanguage: 'zh-CN',
    about: {
      '@type': 'Thing',
      name: '膀胱健康与憋尿控制',
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbItems.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.label,
      ...(item.path ? { item: `https://bladderhealth.cn${item.path}` } : {}),
    })),
  };

  // Parse content HTML for rendering
  const contentHtml = article.content
    .replace(/<h2/g, '<h2 class="font-heading text-lg md:text-xl mt-8 mb-3 text-foreground-950"')
    .replace(/<h3/g, '<h3 class="font-heading text-base md:text-lg mt-6 mb-2 text-foreground-900"')
    .replace(/<p>/g, '<p class="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">')
    .replace(/<ul>/g, '<ul class="list-disc pl-5 mb-4 space-y-1.5">')
    .replace(/<ol>/g, '<ol class="list-decimal pl-5 mb-4 space-y-1.5">')
    .replace(/<li>/g, '<li class="text-sm md:text-base text-foreground-700 leading-relaxed">')
    .replace(/<strong>/g, '<strong class="font-semibold text-foreground-900">')
    .replace(/<table>/g, '<table class="w-full border-collapse mb-4 text-sm">')
    .replace(/<th>/g, '<th class="border border-background-300 p-2 md:p-3 text-left bg-background-100 font-medium text-foreground-900">')
    .replace(/<td>/g, '<td class="border border-background-300 p-2 md:p-3 text-foreground-700">');

  return (
    <>
      <SchemaOrg data={articleSchema} />
      <SchemaOrg data={breadcrumbSchema} />
      <ScrollToTop />
      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-24 md:pt-28 pb-12 md:pb-16">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              {/* Breadcrumb */}
              <div className="mb-6">
                <Breadcrumb items={breadcrumbItems} />
              </div>

              {/* Article Header */}
              <header className="mb-8">
                <div className="flex items-center gap-3 mb-4 flex-wrap">
                  {category && (
                    <Link
                      to={`/category/${category.slug}`}
                      className="text-xs px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-800 hover:bg-secondary-200 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      {category.name}
                    </Link>
                  )}
                  <span className="text-xs text-foreground-400 flex items-center gap-1 whitespace-nowrap">
                    <i className="ri-time-line w-3 h-3 flex items-center justify-center"></i>
                    {article.readTime} 分钟阅读
                  </span>
                  <span className="text-xs text-foreground-400 whitespace-nowrap">
                    {article.publishDate}
                  </span>
                </div>
                <h1 className="font-heading text-xl md:text-3xl lg:text-4xl text-foreground-950 leading-tight">
                  {article.title}
                </h1>
                <p className="mt-3 text-sm md:text-base text-foreground-600 leading-relaxed">
                  {article.excerpt}
                </p>
                <div className="mt-4 flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-primary-100 flex items-center justify-center">
                    <i className="ri-user-line text-sm text-primary-600"></i>
                  </div>
                  <span className="text-sm text-foreground-700 font-medium">{article.author}</span>
                </div>
              </header>

              {/* Featured Image */}
              <div className="mb-8 rounded-lg overflow-hidden">
                <img
                  src={imageUrl}
                  alt={article.title}
                  title={`${article.title} - 膀胱健康管理`}
                  className="w-full aspect-[16/9] object-cover"
                />
              </div>

              {/* Article Content */}
              <article
                className="prose-content"
                dangerouslySetInnerHTML={{ __html: contentHtml }}
              />

              {/* Tags */}
              <div className="mt-10 pt-6 border-t border-background-200/60">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs text-foreground-500 whitespace-nowrap">标签：</span>
                  {article.tags.map((tag) => (
                    <span
                      key={tag}
                      className="text-xs px-2.5 py-1 rounded-full bg-background-100 text-foreground-600"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>

              {/* Related Articles */}
              {relatedArticles.length > 0 && (
                <section className="mt-12">
                  <h2 className="font-heading text-lg md:text-xl text-foreground-950 mb-5">
                    相关文章
                  </h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
                    {relatedArticles.map((rel) => {
                      const relCat = categories.find((c) => c.slug === rel.category);
                      return (
                        <ArticleCard
                          key={rel.slug}
                          article={rel}
                          categoryName={relCat?.name || rel.category}
                        />
                      );
                    })}
                  </div>
                </section>
              )}

              {/* Back to category */}
              <div className="mt-10 text-center">
                <Link
                  to={`/category/${article.category}`}
                  className="inline-flex items-center gap-1.5 text-sm text-primary-600 hover:text-primary-700 font-medium cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center"></i>
                  返回 {category?.name || '分类'}
                </Link>
              </div>
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}